import React, { useMemo, useState } from "react";
import PageLoader from "components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import { Modal } from "@beleef/ui";
import Table from "components/ui/Table";
import Button from "components/ui/Button";
import {
  useCreateDivisionMutation,
  useDeleteDivisionMutation,
  useGetDivisionByIdQuery,
  useGetDivisionsQuery,
  useUpdateDivisionMutation,
} from "apis/division";
import DivisionEdit from "components/division/DivisionEdit";
import DivisionView from "components/division/DivisionView";
import {
  GROUP_OVERRIDE_SETTINGS,
  STATUS_PILL,
  formatNetworkDate,
  getControlLevels,
} from "utils/network";

const PolicyPill = ({ label, className }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
  >
    {label}
  </span>
);

const Divisions = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetDivisionsQuery();

  const { mutate: createDivision, isPending: isCreateLoading } =
    useCreateDivisionMutation();
  const { mutate: updateDivision } = useUpdateDivisionMutation();
  const { mutate: deleteDivision, isPending: isDeleteLoading } =
    useDeleteDivisionMutation();

  // The list response has no populated network on it — only the by-id endpoint
  // returns them, so the detail view fetches when its modal opens.
  const { data: divisionDetail } = useGetDivisionByIdQuery(
    isViewOpen ? selectedId : null,
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Division",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "networkId",
        header: "Network",
        cell: (row) => <span>{row.networkId?.name || "-"}</span>,
        isFilter: false,
        minWidth: "150px",
      },
      {
        accessorKey: "governs",
        header: "Governs",
        // The jurisdictions are the point of the row — a division with none is
        // inert, so it reads as unfinished rather than as empty.
        cell: (row) =>
          row.governs?.length ? (
            <div className="flex flex-wrap gap-1">
              {row.governs.map((code) => (
                <PolicyPill
                  key={code}
                  label={code}
                  className="bg-blue-100 text-blue-800 font-mono"
                />
              ))}
            </div>
          ) : (
            <span className="text-sm text-red-500">Not set</span>
          ),
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status || "active";
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                STATUS_PILL[status] || STATUS_PILL.active
              }`}
            >
              {status}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "controlledBy",
        header: "Narrowed",
        // Only what this division changed — inheriting is the norm, so listing
        // every setting would bury the exceptions.
        cell: (row) => {
          const levels = getControlLevels(row);
          const narrowed = GROUP_OVERRIDE_SETTINGS.filter((setting) =>
            (row.controlledBy || []).some((entry) => entry.path === setting.key),
          );
          return narrowed.length === 0 ? (
            <span className="text-sm text-darkgray">Inherits network</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {narrowed.map((setting) => (
                <PolicyPill
                  key={setting.key}
                  label={`${setting.name}: ${levels[setting.key]}`}
                  className="bg-amber-100 text-amber-800"
                />
              ))}
            </div>
          );
        },
        isFilter: false,
        minWidth: "200px",
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => <span>{formatNetworkDate(row.createdAt)}</span>,
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Division"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Division"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading divisions: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedDivision = data?.find((division) => division._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateDivision({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createDivision(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteDivision(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddDivisionButton = () => (
    <Button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Division
    </Button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Division"
        className="max-w-5xl"
      >
        <DivisionEdit
          key={selectedId || "division-edit"}
          initialData={selectedDivision}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Division"
        className="max-w-5xl"
      >
        <DivisionEdit
          key={isAddOpen ? "division-add-open" : "division-add-closed"}
          initialData={{}}
          onSubmit={handleCreateSubmit}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Division"
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="black-button"
              loading={isDeleteLoading}
            >
              Delete Division
            </Button>
          </div>
        }
      >
        <p>
          Are you sure you want to delete "{selectedDivision?.name}"? This
          action cannot be undone.
        </p>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Division Details"
        className="max-w-5xl"
      >
        <DivisionView division={divisionDetail || selectedDivision} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Divisions"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddDivisionButton}
          emptyText="No divisions found"
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Divisions;
